import { CombinedGraphQLErrors } from '@apollo/client/errors';
import retry from 'async-retry';

import type { MaterializeOutcome } from './usePlaybookMaterialize';

/**
 * Retry policy for recording an accept. The edit is already in the document by
 * the time this runs, so a response lost in transit leaves the server without
 * the decision behind it: a couple of fast attempts close that window without
 * making the button feel stuck. Server rejections bail on the first response,
 * so only transport failures ever spend this budget.
 */
export const REVIEW_RECORD_RETRY = {
  factor: 2,
  maxTimeout: 1_000,
  minTimeout: 250,
  randomize: true,
  retries: 2,
};

type BlockedMaterializeType = Exclude<MaterializeOutcome['type'], 'materialized'>;

interface ReviewAcceptedSuggestionOptions<TResult> {
  readonly addPendingAccepted: () => void;
  readonly materialize: () => Promise<MaterializeOutcome>;
  readonly onEditorNotReady: () => void;
  readonly onMaterializeFailed: () => void;
  readonly onReviewFailed: () => void;
  readonly onSigningLocked: () => void;
  readonly onStale: () => void;
  readonly recordReview: () => Promise<TResult>;
  readonly removePendingAccepted: () => void;
}

/**
 * One accept: materialize into the document, then record the decision.
 *
 * `pendingAccepted` covers the gap between those two steps. Once materialization
 * succeeds, keep that overlay until recording succeeds: the edit has changed the
 * document, so dropping it would stale the suggestion and let auto-rerun replace
 * the proposal that still needs its decision recorded.
 */
export async function reviewAcceptedSuggestion<TResult>({
  addPendingAccepted,
  materialize,
  onEditorNotReady,
  onMaterializeFailed,
  onReviewFailed,
  onSigningLocked,
  onStale,
  recordReview,
  removePendingAccepted,
}: ReviewAcceptedSuggestionOptions<TResult>): Promise<TResult | undefined> {
  const dispatchBlockedSnack: Record<BlockedMaterializeType, () => void> = {
    'not-ready': onEditorNotReady,
    'signing-locked': onSigningLocked,
    stale: onStale,
  };

  addPendingAccepted();
  const outcome = await materialize().catch(error => {
    removePendingAccepted();
    throw error;
  });
  // Refused before the document was touched, so there is no edit to cover.
  if (outcome.type !== 'materialized') {
    removePendingAccepted();
    dispatchBlockedSnack[outcome.type]();
    return;
  }

  // Some edits failed to apply, so let the user retry or rerun the rule.
  if (outcome.errorCount > 0) {
    removePendingAccepted();
    onMaterializeFailed();
    return;
  }

  const result = await retry(async () => {
    try {
      return await recordReview();
    } catch (error) {
      /**
       * A GraphQL-level rejection is the server's verdict on this decision
       * (rule already resolved, a sibling position under review, suggestion
       * gone), so retrying only delays the snack. A lost or failed response
       * carries no `errors` array and stays retryable, which is the case the
       * retry exists for.
       */
      if (CombinedGraphQLErrors.is(error)) throw Object.assign(error, { bail: true });
      throw error;
    }
  }, REVIEW_RECORD_RETRY).catch(error => {
    onReviewFailed();
    throw error;
  });

  removePendingAccepted();
  return result;
}
